import { FeatureTypes } from "entity-app/constants";
import { useContext, useMemo, useState } from "react";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_APPLY_LINKING_CHANGES } from "services/graphQL/mutations";
import { ProjectContext } from "context/ProjectProvider";
import { message } from "antd";
import { ErrorMessages, SuccessMessages } from "constants/index";
import { TEditingEntity, TEntityLink } from "../models";
import { getFeatureDetail, translateEntityInfoIntoBlockCards } from "../utils";
import LinkedFeatureItems from "./linked-feature-items";
import { generateLinkingPayload } from "../utils/generate-payload";
import { MUTATION_UPDATE_MATERIAL_TRACKING } from "../queries/mutation";
import { UnsaveConfirmModal } from "./unsave-confirm-dialog";
import ErrorPopUpComponent, { ErrorResponse } from "./error-pop-up";

function LeftSideLinkData({
  selectedEntityInfo,
  leftTabEntityTypes,
  editingEntity,
  setEditingEntity,
  refetchEntityInfo,
  featureTypeId
}: {
  selectedEntityInfo: TEntityLink | undefined;
  leftTabEntityTypes: FeatureTypes[];
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  refetchEntityInfo: () => Promise<void>;
  featureTypeId: FeatureTypes;
}) {
  const { gqlClientForProject } = useContext(ProjectContext);
  const [whileSaving, setWhileSaving] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorPayload, setErrorPayload] = useState<ErrorResponse | null>(null);

  const [linkingMutation] = useCIQMutation(MUTATION_APPLY_LINKING_CHANGES, {
    client: gqlClientForProject
  });
  const [updateMaterialTrackingMutation] = useCIQMutation(
    MUTATION_UPDATE_MATERIAL_TRACKING,
    {
      client: gqlClientForProject
    }
  );

  const handleEdit = (entity: TEditingEntity) => {
    setEditingEntity(entity);
  };

  const handleCancel = () => {
    refetchEntityInfo();
    setEditingEntity(null);
  };

  const blockListData = useMemo(() => {
    if (selectedEntityInfo)
      return translateEntityInfoIntoBlockCards({
        entityInfo: selectedEntityInfo,
        leftTabEntityTypes
      });
    return [];
  }, [leftTabEntityTypes, selectedEntityInfo]);

  const handleSave = async () => {
    if (!selectedEntityInfo || !editingEntity) return;
    setWhileSaving(true);

    const cardsFeatureName = editingEntity.featureDetail.feature;
    const oldEntityLinks = blockListData.find(
      (x) => x.featureDetail.feature === cardsFeatureName
    )?.list!;
    const newEntityLinks = editingEntity.list;

    const payload = generateLinkingPayload({
      selectedEntityInfo: {
        ids: selectedEntityInfo.ids,
        parentFeatureDetail: getFeatureDetail(
          selectedEntityInfo.feature_type_id
        ).feature,
        predecessors: selectedEntityInfo.predecessor_material_links
      },
      oldEntityLinks,
      newEntityLinks,
      cardsFeatureName
    });

    if (payload.offMaterialTrackingOfSubmittalIds.length) {
      // Make material tracking ON for these submittals
      await updateMaterialTrackingMutation({
        variables: {
          featureInstanceIds: payload.offMaterialTrackingOfSubmittalIds,
          material_tracking: true
        }
      });
    }

    const payloadData = {
      input: {
        inserts: payload.inserts,
        deletes: payload.deletes,
        updates: []
      },
      metadata: {
        request_source: "LINKING_PAGE"
      }
    };

    const linkingResponse = await linkingMutation(
      {
        variables: payloadData
      },
      { showError: false }
    );

    if (linkingResponse?.data) {
      message.success(SuccessMessages.SuccessfullyUpdate);

      await refetchEntityInfo();
      setEditingEntity(null);
    } else if (
      linkingResponse?.error &&
      linkingResponse?.error?.extensions?.code === "GraphCycleError"
    ) {
      setErrorPayload({ error: linkingResponse.error });
      setIsErrorModalOpen(true);
      setWhileSaving(false);
    } else {
      message.error(ErrorMessages.SomethingWentWrong);
    }
    setWhileSaving(false);
  };

  return (
    <div className="w-full h-[calc(100vh-155px)] flex flex-col bg-ciq-gray-600">
      {selectedEntityInfo &&
        blockListData.map(
          ({ list, featureDetail, entityDetail, parentEntity }) => {
            const isEditing =
              editingEntity !== null &&
              editingEntity.featureDetail.feature === featureDetail.feature;
            const isDisabled = editingEntity !== null && !isEditing;
            return (
              <LinkedFeatureItems
                key={featureDetail.feature}
                list={isEditing ? editingEntity.list : list}
                featureDetail={featureDetail}
                isEditing={isEditing}
                isDisabled={isDisabled}
                onEdit={() =>
                  handleEdit({
                    list,
                    featureDetail,
                    entityDetail,
                    parentEntity,
                    height: 0
                  })
                }
                onCancel={handleCancel}
                onSave={handleSave}
                editingEntity={editingEntity}
                setEditingEntity={setEditingEntity}
                refetchEntityInfo={refetchEntityInfo}
                selectedEntityInfo={selectedEntityInfo}
                featureTypeId={featureTypeId}
              />
            );
          }
        )}
      <UnsaveConfirmModal editingEntity={editingEntity} onSave={handleSave} />
      {isErrorModalOpen && errorPayload && (
        <ErrorPopUpComponent
          data={errorPayload}
          open={isErrorModalOpen}
          onProceed={() => setIsErrorModalOpen(false)}
          onClose={() => setIsErrorModalOpen(false)}
        />
      )}
      {whileSaving && (
        <div className="absolute w-full h-full bg-black bg-opacity-0 z-50" />
      )}
    </div>
  );
}
export default LeftSideLinkData;
